import { DataFactory } from "n3";
import type { ModelContext } from "../../rdf/model";
import type { TermSummary } from "../../rdf/types";

const { namedNode } = DataFactory;

export interface HierarchyNode {
  term: TermSummary;
  children: HierarchyNode[];
}

function sortByLabel(a: HierarchyNode, b: HierarchyNode): number {
  return (a.term.label ?? a.term.prefixedName).localeCompare(
    b.term.label ?? b.term.prefixedName,
  );
}

/**
 * Builds a tree from a flat term list plus a hierarchy predicate
 * (`rdfs:subClassOf`/`rdfs:subPropertyOf`), restricted to parent/child pairs
 * where *both* ends are in `terms` (so toggling a dataset off in the
 * switcher also prunes the tree, not just the flat list). Multiple
 * inheritance in the underlying OWL/RDFS graph means a term can legitimately
 * appear under more than one parent — rendered as a tree (not deduplicated
 * into a DAG view) for simplicity, matching how Brick's own explorer does it.
 */
export function buildHierarchy(
  ctx: ModelContext,
  terms: TermSummary[],
  relationPredicateIri: string,
): HierarchyNode[] {
  const termsByIri = new Map(terms.map((t) => [t.iri, t]));
  const childIrisByParent = new Map<string, string[]>();
  const parentCountByChild = new Map<string, number>();

  for (const term of terms) {
    const parentIris = ctx.store
      .getObjects(namedNode(term.iri), namedNode(relationPredicateIri), null)
      .filter((o) => o.termType === "NamedNode")
      .map((o) => o.value)
      .filter((iri) => termsByIri.has(iri) && iri !== term.iri);

    parentCountByChild.set(term.iri, parentIris.length);
    for (const parentIri of parentIris) {
      const existing = childIrisByParent.get(parentIri);
      if (existing) existing.push(term.iri);
      else childIrisByParent.set(parentIri, [term.iri]);
    }
  }

  function build(iri: string, ancestry: ReadonlySet<string>): HierarchyNode {
    const term = termsByIri.get(iri)!;
    const childIris = childIrisByParent.get(iri) ?? [];
    const children = childIris
      // Guards against a malformed/cyclic subClassOf chain infinitely recursing.
      .filter((childIri) => !ancestry.has(childIri))
      .map((childIri) => build(childIri, new Set([...ancestry, childIri])));
    children.sort(sortByLabel);
    return { term, children };
  }

  const roots = terms
    .filter((term) => (parentCountByChild.get(term.iri) ?? 0) === 0)
    .map((term) => build(term.iri, new Set([term.iri])));
  roots.sort(sortByLabel);
  return roots;
}

/**
 * Walks upward from `iri` via `relationPredicateIri`, staying within
 * `termsByIri`, to find a path of ancestor IRIs — used purely to
 * auto-expand the tree far enough to reveal a term selected via search or a
 * deep link. Only one parent is followed at each step (arbitrary tie-break
 * on multiple inheritance); revealing *a* path is enough for the UX goal.
 */
export function getAncestorPath(
  ctx: ModelContext,
  iri: string,
  relationPredicateIri: string,
  termsByIri: ReadonlyMap<string, TermSummary>,
): string[] {
  const path: string[] = [];
  const visited = new Set<string>([iri]);
  let current = iri;

  for (let depth = 0; depth < 64; depth++) {
    const parentIris = ctx.store
      .getObjects(namedNode(current), namedNode(relationPredicateIri), null)
      .filter((o) => o.termType === "NamedNode")
      .map((o) => o.value)
      .filter((candidate) => termsByIri.has(candidate) && !visited.has(candidate));

    const nextParent = parentIris[0];
    if (!nextParent) break;
    path.push(nextParent);
    visited.add(nextParent);
    current = nextParent;
  }

  return path;
}
